import {
  CheckCircleIcon,
  CloseCircleIcon,
  ExclamationIcon,
  InfoCircleSolidIcon,
  IconTypeProps,
} from '@mm/icons';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import { colors, ColorType, getColorStylesByType } from '@mm/utils';
import './Alert.pcss';

type Props = {
  children?: string | ReactNode | Element[];
  className?: string;
  css?: {
    container?: string;
    text?: string;
  };
  showInfoIcon?: boolean;
  showCloseIcon?: boolean;
  type?: ColorType;
  closeIconProps?: IconTypeProps;
  borderRadius?: number;
  onClick?: () => any;
  showHeader?: boolean;
  hideLeftIcon?: boolean;
  leftIcon?: React.ReactNode;
};

const AlertInner = (props: Props) => {
  const {
    css,
    children,
    showInfoIcon,
    showCloseIcon = true,
    closeIconProps = {},
    type = 'warning',
    borderRadius = 5,
    onClick,
    showHeader,
    hideLeftIcon = false,
    leftIcon,
  } = props;

  const { className: closeIconClassName, ...restCloseIconProps } =
    closeIconProps;
  const computedClassName = classNames(css?.container, 'c-alert', {
    'c-alert--clickable': onClick,
  });

  const { backgroundColor, color, borderColor } = getColorStylesByType(type);
  const style = { backgroundColor, borderColor, borderRadius };
  const alertHeader = (
    <div className="с-alert__header">
      {!hideLeftIcon && (
        <>
          {type !== 'success' && (
            <ExclamationIcon color={color} className="с-alert__left-icon" />
          )}
          {showInfoIcon && type === 'success' && (
            <CheckCircleIcon color={color} className="с-alert__left-icon" />
          )}
        </>
      )}
      {closeIconProps && (
        <CloseCircleIcon
          color={type === 'error' ? colors.red : colors.palePurple}
          className={classNames('c-alert__close-icon', closeIconClassName)}
          {...restCloseIconProps}
        />
      )}
    </div>
  );

  if (showHeader) {
    return (
      <div className={computedClassName} style={style} onClick={onClick}>
        {alertHeader}
        {children && (
          <span className={classNames(css?.text, 'c-alert__text')}>
            {children}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={computedClassName} style={style} onClick={onClick}>
      {!hideLeftIcon && (
        <>
          {type === 'info' && (
            <InfoCircleSolidIcon color={color} className="с-alert__left-icon" />
          )}
          {type !== 'success' && type !== 'info' && (
            <ExclamationIcon color={color} className="с-alert__left-icon" />
          )}
          {showInfoIcon && type === 'success' && (
            <CheckCircleIcon color={color} className="с-alert__left-icon" />
          )}
        </>
      )}
      {leftIcon && leftIcon}
      {children && (
        <span className={classNames(css?.text, 'c-alert__text')}>
          {children}
        </span>
      )}

      {showCloseIcon && (
        <CloseCircleIcon
          color={type === 'error' ? colors.red : colors.palePurple}
          className={classNames('c-alert__close-icon', closeIconClassName)}
          {...restCloseIconProps}
        />
      )}
    </div>
  );
};

// to provide support for nested styles
export const Alert = (props: Props) => {
  const { className, css } = props;
  return (
    <AlertInner
      {...props}
      css={{
        ...css,
        container: classNames(className, css?.container),
      }}
    />
  );
};
